<template lang="pug">
.add-gym-equipment
  StepHeader(
    :title="`Step ${step}: Add Gym Equipment`"
    description="Add Rogue equipment available at your gym by selecting items from your order history or searching for specific equipment.")
  RadioButtonGroup.source-toggle(name="productSource" v-model="productSource" :options="sourceOptions")
  loading(v-if="isLoading")
  .order-history(v-else-if="productSource === 'orderHistory'")
    RogueSelect.history-select(
      label="Product Purchased in"
      :options="filterOptions"
      v-model="filterModel"
    )
    Carousel.order-history-carousel(v-if="uniqueProducts.length")
      .product-selections
        .product-selection(v-for="product in uniqueProducts")
          StaticImage(:src='product.thumbnail' :width="84" :alt='product.name' lazy)
          .name(v-html='product.name || ""')
          Checkbox.checkbox(
            :id="`order-history-checkbox-'${product.productId}`" :modelValue="modelValue.map(p => p.productId).includes(product.productId)"
            @update:modelValue="toggleProductSelection(product, $event)"
          )
    .no-results(v-else)
      h3 {{__("No Results")}}
  .search-box(v-else-if="productSource === 'search'" tabindex="-1" @focusout="hide($event)")
    RogueInput.search-input(
      v-model="query"
      placeholder="Search" icon="search" iconPosition="right"
      @focusin="showResults = true"
    )
    .search-results(v-if="showResults")
      .no-results(v-if="emptySearchResults && initialLoad")
        h3 {{__("No Results")}}
      .search-result(
        :productId="product.productId"
        v-for="(product,index) in searchProducts"
        :key="product.productId"
        :class="!product.productId && 'loading'"
      )
        StaticImage(:src='product.thumbnail' :width="200" :alt='product.name' lazy)
        .name(v-html='product.name || ""')
        Checkbox.checkbox(
          :id="`equipment-results-checkbox-${product.productId}`" :modelValue="modelValue.map(p => p.productId).includes(product.productId)"
          @update:modelValue="toggleProductSelection(product, $event)"
        )
  h4 {{__('Selected Products')}}
  p.selected-count {{ `${modelValue.length} products selected.`}}
  Carousel
    .product-selections
      .product-selection(v-for="product in modelValue")
        StaticImage(:src='product.thumbnail' :width="200" :alt='product.name' lazy)
        .name(v-html='product.name || ""')
        Checkbox.checkbox(
          :id="`product-selections-checkbox-${product.productId}`" :modelValue="modelValue.map(p => p.productId).includes(product.productId)"
          @update:modelValue="toggleProductSelection(product, $event)"
        )
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { IGymProduct, IOrder, IOrderItem } from '@rogue/universal'
import { MS_ADDON_SKUS, MS_BOLT_KIT_SKUS } from '../product/rackbuilder/utils/ms-utils/ms-constants'
import StepHeader from './StepHeader.vue'
import { useRogue } from '@/js/services/Rogue'
import { type IReflektionProcessedProduct, widgets } from '@/js/services/rfk/types'
import { PODS, SECTIONS } from '@/js/services/cnstrc/constants'
import { fetchOrders } from '@/js/helpers/fetchOrders'
import { useProductStore } from '@/js/stores/productStore'
import { MMS_ADDON_SKU, MMS_SHARED_BOLT_KIT_SKU, MMS_SOLO_BOLT_KIT_SKU } from '@/js/pages/product/rackbuilder/utils/mms-utils/mms-constants'

const rogue = useRogue()
const route = useRoute()
const productStore = useProductStore()

const props = defineProps<{
  modelValue: IGymProduct[],
  isEdit: boolean,
  step: number,
}>()

const emit = defineEmits<{(e: 'update:modelValue', products: typeof props.modelValue): void }>()

const productSource = ref<'orderHistory' | 'search'>('orderHistory')
const sourceOptions = [{
  label : 'Order History',
  value : 'orderHistory',
}, {
  label : 'Search Equipment',
  value : 'search',
}]

const isLoading = ref<boolean>(true)
const showResults = ref<boolean>(false)
const emptySearchResults = ref<boolean>(true)
const initialLoad = ref<boolean>(false)
const query = ref<string>('')
const showing = ref<string | null>(null)
const loadingQuery = ref<string | null>(null)
const searchProducts = ref<IReflektionProcessedProduct[]>([])
const orders = ref<Partial<IOrder>[]>([])

const filterModel = ref('all') // default to `All Time`
const filterOptions = [
  {
    label : 'All time',
    value : 'all',
  },
  {
    label : 'Last 30 days',
    value : '1m',
  },
  {
    label : 'Last 6 months',
    value : '6m',
  },
]

function isSince(dateString: string, monthsAgo: number) {
  const orderDate = new Date(dateString)
  const sinceDate = new Date()
  sinceDate.setMonth(sinceDate.getMonth() - monthsAgo)

  return orderDate >= sinceDate
}

const products = computed(() => productStore.products)

const buildUrl = (url: string, recipeId?: string): string => (recipeId ? `${url}?rid=${recipeId}` : url)

const STORAGE_PRODUCTS = [MMS_ADDON_SKU, MMS_SHARED_BOLT_KIT_SKU, MMS_SOLO_BOLT_KIT_SKU, ...MS_ADDON_SKUS, ...MS_BOLT_KIT_SKUS]

const purchasedProducts = computed<IGymProduct[]>(() => orders.value
  .filter(o => !o.isCancelled)
  .filter(o => filterModel.value === 'all'
    || (filterModel.value === '1m' && isSince(o.createdAt, 1))
    || (filterModel.value === '6m' && isSince(o.createdAt, 6)))
  .flatMap(o => o.items || [])
  .filter(item => !!products.value[item.productId])
  .filter(item => !STORAGE_PRODUCTS.includes(item.sku))
  .filter(item => products.value[item.productId].type !== 'virtual')
  .map(item => ({
    productId : item.productId,
    name      : item.name || products.value[item.productId]?.name,
    thumbnail : item.thumbnail!,
    sku       : item.sku,
    url       : buildUrl(products.value[item.productId]?.url || '', item.metadata?.recipeId),
    storeId   : rogue.site.defaultStoreId,
  })))

const uniqueProducts = computed(() => Array.from(
  new Map(purchasedProducts.value.map(p => [p.productId, p])).values()
))

function hide(evt: any) {
  // if the blur was because of outside focus
  // currentTarget is the parent element, relatedTarget is the clicked element
  if (!evt || !evt.currentTarget.contains(evt.relatedTarget)) {
    showResults.value = false
  }
}

async function getSearchResults() {
  // Already showing results for this query
  if (query.value === showing.value || query.value === loadingQuery.value) {
    return
  }

  loadingQuery.value = query.value

  const useConstructor = rogue.featureFlags?.isOn('enable_constructor')

  const request = useConstructor
    ? query.value
      ? rogue.cnstrc.getAutocompleteResults(query.value, 16, SECTIONS.PRODUCTS)
      : rogue.cnstrc.getRecommendations(PODS.SEARCH_DEFAULT, 16)
    : rogue.rfk?.fetch({ widget: widgets.SEARCH_PREVIEW, uri: route.path, n_item: 16, query: query.value }, 1)

  const resp = await request

  // Updated again after response received
  if (query.value !== loadingQuery.value) {
    return
  }

  loadingQuery.value = null
  showing.value = query.value

  if (resp?.content?.product?.value?.length) {
    searchProducts.value = resp.content.product.value
    emptySearchResults.value = false
  } else {
    searchProducts.value = []
    emptySearchResults.value = true
  }

  initialLoad.value = true
}

function toggleProductSelection(product: IGymProduct, isSelected: boolean) {
  if (isSelected && !props.modelValue.map(p => p.productId).includes(product.productId)) {
    const newSelection: IGymProduct[] = Object.assign(props.modelValue)
    newSelection.push({
      productId : product.productId,
      name      : product.name,
      thumbnail : product.thumbnail!,
      sku       : product.sku,
      url       : product.url,
      storeId   : rogue.site.defaultStoreId,
    })
    emit('update:modelValue', newSelection)
  } else if (!isSelected && props.modelValue.map(p => p.productId).includes(product.productId)) {
    emit('update:modelValue', props.modelValue.filter(p => p.productId !== product.productId))
  }
}

let updateTimeout: any = null
watch(query, () => {
  clearTimeout(updateTimeout)
  updateTimeout = setTimeout(() => getSearchResults(), 250)
}, {
  immediate: true,
})

function getProductsToLoad(item: IOrderItem): number[] {
  const toLoad = [item.productId]

  if (item.metadata?.groupedProductId) {
    toLoad.push(item.metadata?.groupedProductId)
  }

  item.children?.forEach(child => {
    toLoad.push(child.productId)
    child.children?.forEach(grandchild => toLoad.push(grandchild.productId))
  })

  return toLoad
}

onMounted(async () => {
  const resp = await fetchOrders(rogue)
  orders.value = resp.orders
  const productIds: number[] = [...new Set(orders.value.flatMap(o => (o.items || [])).flatMap(i => getProductsToLoad(i)) || [])]
  await productStore.loadAll(productIds, rogue)
  isLoading.value = false
})
</script>
<style lang="scss" scoped>
.add-gym-equipment {
  margin: $space-m 0;

  .source-toggle {
    margin-bottom: $space-s;
  }

  .order-history {
    border-bottom: 1px solid $color-gray;
    margin-bottom: $space-s;

    .history-select, &-carousel {
      margin-bottom: $space-s;
    }
  }

  .search-box {
    border-bottom: 1px solid $color-gray;
    margin-bottom: $space-s;
    position: relative;

    .search-input {
      margin-bottom: $space-s;
    }
  }

  h4 {
    margin-bottom: $space-xs;
  }
}

.selected-count {
  color: $color-black-light;
}

$gap: $space-s;

.search-results {
  background-color: $color-white;
  border: 1px solid $color-gray;
  border-radius: 0;
  display: flex;
  flex-wrap: wrap;
  gap: $space-s;
  max-height: 315px;
  left: 0;
  overflow-y: auto;
  padding: $space-s;
  position: absolute;
  right: 0;
  top: 42px;
  z-index: 14;
}

.search-result {
  --search-result-columns: 3;
  --search-result-gap-count: calc( var(--search-result-columns) - 1 );
  display: flex;
  flex-basis: calc(calc(100% / var(--search-result-columns)) - calc($gap / var(--search-result-columns) * var(--search-result-gap-count)));
  flex-direction: column;
  gap: $space-xxxs;
  position: relative;

  .name {
    font-size: $size-xs;
    line-height: $size-s;
  }

  .checkbox {
    left: $space-xxxs;
    position: absolute;
    top: $space-xxxs;
  }
}

.product-selections {
  display: flex;
  flex-direction: row;
  gap: $space-s;

  .product-selection {
    display: flex;
    flex-direction: column;
    gap: $space-xxs;
    position: relative;
    width: 84px;

    .name {
      font-size: $size-xs;
      line-height: $size-s;
    }

    .checkbox {
      left: $space-xxxs;
      position: absolute;
      top: $space-xxxs;
    }

    @include mobile() {
      --product-selection-columns: 3;
    }
  }
}
</style>
